import axios from 'axios';

const API_URL = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

/**
 * Campaign Service - Handles all API calls for the campaign system
 */

// ============================================
// TEMPLATE API
// ============================================

export const templateAPI = {
    async getPrebuiltTemplates() {
        const response = await axios.get(`${API_URL}/campaigns/templates/prebuilt`);
        return response.data.templates;
    },

    async createFromPrebuilt(expertId, prebuiltId, customizations) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/templates/from-prebuilt`, {
            prebuiltId,
            ...customizations
        });
        return response.data.template;
    },

    async createTemplate(expertId, templateData) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/templates`, templateData);
        return response.data.template;
    },

    async getTemplates(expertId) {
        const response = await axios.get(`${API_URL}/campaigns/${expertId}/templates`);
        return response.data.templates;
    },

    async getTemplate(expertId, templateId) {
        const response = await axios.get(`${API_URL}/campaigns/${expertId}/templates/${templateId}`);
        return response.data.template;
    },

    async updateTemplate(expertId, templateId, updates) {
        const response = await axios.put(`${API_URL}/campaigns/${expertId}/templates/${templateId}`, updates);
        return response.data.template;
    },

    async deleteTemplate(expertId, templateId) {
        const response = await axios.delete(`${API_URL}/campaigns/${expertId}/templates/${templateId}`);
        return response.data.success;
    },

    async previewTemplate(expertId, templateId) {
        const response = await axios.get(`${API_URL}/campaigns/${expertId}/templates/${templateId}/preview`);
        return response.data.html;
    }
};

// ============================================
// SEGMENT API
// ============================================

export const segmentAPI = {
    async getPrebuiltSegments() {
        const response = await axios.get(`${API_URL}/campaigns/segments/prebuilt`);
        return response.data.segments;
    },

    async createFromPrebuilt(expertId, prebuiltId) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/segments/from-prebuilt`, {
            prebuiltId
        });
        return response.data.segment;
    },

    async createSegment(expertId, segmentData) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/segments`, segmentData);
        return response.data.segment;
    },

    async getSegments(expertId) {
        const response = await axios.get(`${API_URL}/campaigns/${expertId}/segments`);
        return response.data.segments;
    },

    async getSegment(expertId, segmentId) {
        const response = await axios.get(`${API_URL}/campaigns/${expertId}/segments/${segmentId}`);
        return response.data.segment;
    },

    async updateSegment(expertId, segmentId, updates) {
        const response = await axios.put(`${API_URL}/campaigns/${expertId}/segments/${segmentId}`, updates);
        return response.data.segment;
    },

    async deleteSegment(expertId, segmentId) {
        const response = await axios.delete(`${API_URL}/campaigns/${expertId}/segments/${segmentId}`);
        return response.data.success;
    },

    async previewSegment(expertId, filters) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/segments/preview`, { filters });
        return response.data.preview;
    }
};

// ============================================
// CAMPAIGN API
// ============================================

export const campaignAPI = {
    async createCampaign(expertId, campaignData) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/campaigns`, campaignData);
        return response.data.campaign;
    },

    async getCampaigns(expertId) {
        const response = await axios.get(`${API_URL}/campaigns/${expertId}/campaigns`);
        return response.data.campaigns;
    },

    async getCampaign(expertId, campaignId) {
        const response = await axios.get(`${API_URL}/campaigns/${expertId}/campaigns/${campaignId}`);
        return response.data.campaign;
    },

    async updateCampaign(expertId, campaignId, updates) {
        const response = await axios.put(`${API_URL}/campaigns/${expertId}/campaigns/${campaignId}`, updates);
        return response.data.campaign;
    },

    async deleteCampaign(expertId, campaignId) {
        const response = await axios.delete(`${API_URL}/campaigns/${expertId}/campaigns/${campaignId}`);
        return response.data.success;
    },

    async sendCampaign(expertId, campaignId) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/campaigns/${campaignId}/send`);
        return response.data.execution;
    },

    async pauseCampaign(expertId, campaignId) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/campaigns/${campaignId}/pause`);
        return response.data.campaign;
    },

    async resumeCampaign(expertId, campaignId) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/campaigns/${campaignId}/resume`);
        return response.data.campaign;
    }
};

// ============================================
// UNSUBSCRIBE API
// ============================================

export const unsubscribeAPI = {
    async getUnsubscribedList(expertId) {
        const response = await axios.get(`${API_URL}/campaigns/${expertId}/unsubscribed`);
        return response.data.emails;
    },

    async resubscribe(expertId, email) {
        const response = await axios.post(`${API_URL}/campaigns/${expertId}/resubscribe`, { email });
        return response.data.success;
    }
};
